import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoaderPulse } from '@reservamos/elements';
import { LogOut } from 'lucide-react';
import AvatarUnauth from 'images/avatar-unauth.webp';
import AdultIcon from 'images/portfolio.webp';
import { useCdsFrequentPassenger } from '../../context/CdsFrequentPassenger';
import { cdsFpLoggedOutTracker } from '../../metrics/user-analytics/cdsFrequentPassenger';
import { CDS_FP_AUTH_TAB_SIGNUP, CDS_FP_PASSWORD_PATH } from '../../constants/cdsFrequentPassenger';
import getCdsFpAuthUrl from '../../utils/getCdsFpAuthUrl';
import getCdsFpDisplayName from '../../utils/getCdsFpDisplayName';
import formatCdsFpErrorMessage from '../../utils/formatCdsFpErrorMessage';
import {
  UserMenuSheet,
  UserMenuSheetTrigger,
  UserMenuSheetContent,
} from '../reserhub/user-menu-sheet';
import UserMenuButton from './CdsFpUserMenuButton';
import CdsFpEditIcon from './CdsFpEditIcon';

/**
 * Header user menu for CDS Pasajero Frecuente (login, session, logout).
 * @returns {React.ReactElement|null}
 */
export default function CdsFrequentPassengerUserMenu() {
  const { t } = useTranslation('cdsFrequentPassenger');
  const history = useHistory();
  const { enabled, isAuthenticated, isLoading, isBootstrapped, accountStatus, error, logout } =
    useCdsFrequentPassenger();
  const [open, setOpen] = useState(false);

  const handleLoginClick = useCallback(() => {
    const { pathname, search, hash } = history.location;
    const loginUrl = getCdsFpAuthUrl({
      redirectUrl: `${pathname}${search}${hash}`,
    });

    if (`${history.location.pathname}${history.location.search}` === loginUrl) return;

    history.push(loginUrl);
  }, [history]);

  const handleRegisterClick = useCallback(() => {
    const signupUrl = getCdsFpAuthUrl({ tab: CDS_FP_AUTH_TAB_SIGNUP });

    if (`${history.location.pathname}${history.location.search}` === signupUrl) return;

    history.push(signupUrl);
  }, [history]);

  const handleChangePasswordClick = useCallback(() => {
    setOpen(false);
    history.push(CDS_FP_PASSWORD_PATH);
  }, [history]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      cdsFpLoggedOutTracker();
      setOpen(false);
    } catch {
      // Keep the menu open and the account visible: the server never confirmed sign-out,
      // so the local session must not look logged out until logout() succeeds.
    }
  }, [logout]);

  if (!enabled) return null;
  if (!isBootstrapped || isLoading) return <LoaderPulse isAccent size="S" />;

  const displayName = getCdsFpDisplayName(accountStatus);
  const avatar = isAuthenticated ? AdultIcon : AvatarUnauth;
  const label = isAuthenticated ? displayName || t('logged_in_label') : t('login');

  return (
    <div className="bg-gradient-primary max-w-fit rounded-full backdrop-blur-lg flex items-center shadow-md">
      <div className="relative flex items-center justify-center bg-white/10 p-0.5 md:pr-1 gap-2 backdrop-blur-lg rounded-full">
        <UserMenuSheet open={open} onOpenChange={setOpen}>
          <UserMenuSheetTrigger asChild>
            <UserMenuButton
              avatar={avatar}
              displayText={label}
              onClick={() => {
                if (!isAuthenticated) {
                  handleLoginClick();
                }
              }}
              label={isAuthenticated ? t('open_menu') : t('login')}
              isAuthenticated={isAuthenticated}
              isMenuOpen={open}
            />
          </UserMenuSheetTrigger>

          <UserMenuSheetContent>
            <div className="flex flex-col gap-3 p-4">
              {isAuthenticated ? (
                <>
                  <p className="text-base font-semibold">
                    {displayName || t('pasajero_frecuente')}
                  </p>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-3 text-left text-sm font-medium"
                    onClick={handleChangePasswordClick}
                  >
                    <span>{t('password_update_title')}</span>
                    <CdsFpEditIcon className="h-4 w-4 shrink-0 text-[#9B9B9B]" />
                  </button>
                  <button
                    type="button"
                    className="flex items-center gap-2 text-left text-sm font-medium"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4" />
                    {t('logout')}
                  </button>
                  {error && (
                    <p className="text-sm text-red-600">{formatCdsFpErrorMessage(t, error)}</p>
                  )}
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="text-left text-sm font-medium"
                    onClick={() => {
                      setOpen(false);
                      handleLoginClick();
                    }}
                  >
                    {t('login')}
                  </button>
                  <button
                    type="button"
                    className="text-left text-sm font-medium"
                    onClick={() => {
                      setOpen(false);
                      handleRegisterClick();
                    }}
                  >
                    {t('register')}
                  </button>
                </>
              )}
            </div>
          </UserMenuSheetContent>
        </UserMenuSheet>

        {isAuthenticated && (
          <button
            type="button"
            className="hidden md:flex bg-white/10 h-8 w-8 hover:text-primary-regular rounded-full text-white hover:bg-white/70 transition-colors duration-200 items-center justify-center"
            aria-label={t('logout')}
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
}
